import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "use-intl";

import { ArrowForwardIcon } from "@/components/appIcons";
import { FormatIcon } from "@/components/format-icon";
import { Link } from "@/components/link";
import type { DealOffer } from "@/components/marketplace/deal-offers-types";
import { getReleaseAlternativePackingQueryOptions } from "@/components/marketplace/release-alternative-packing-query";
import { Price } from "@/components/Price";
import { buildReleaseFormatSummaryDisplay } from "@/lib/release-format-display";

export function ReleaseOfferAlternatives({
  alternatives,
  hasOffer,
  workId,
}: {
  alternatives: DealOffer[];
  hasOffer: boolean;
  workId?: string;
}) {
  const t = useTranslations("shared.affiliates");
  const formats = useTranslations("shared.releaseFilters");
  const formatMessages = useTranslations("shared.formats");
  const packingQuery = useQuery({
    ...getReleaseAlternativePackingQueryOptions({
      workId: workId ?? "",
      releaseIds: alternatives.map((offer) => offer.matchedReleaseIds[0]!),
    }),
    enabled: Boolean(workId) && alternatives.length > 0,
  });
  const packingByReleaseId = new Map(
    (packingQuery.data?.items ?? []).map((release) => [
      release.id,
      buildReleaseFormatSummaryDisplay(release.mediaFormatSummary, { formatMessages }).summaryText,
    ]),
  );
  if (alternatives.length === 0) return null;
  return (
    <div className="border-t border-border px-4 py-3 sm:px-6">
      <h3 className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
        {t("releaseAlternatives", { hasOffer: hasOffer ? "yes" : "no" })}
      </h3>
      <ul className="divide-y divide-border/60">
        {alternatives.map((offer) => (
          <li key={offer.offerId}>
            <Link
              to="/release/$id"
              params={{ id: offer.matchedReleaseIds[0]! }}
              className="flex min-h-16 items-center gap-3 py-3 hover:text-primary"
            >
              <FormatIcon format={offer.formatCategory ?? "other"} size={20} />
              <span className="min-w-0 flex-1">
                <span className="block text-xs font-bold uppercase">
                  {packingByReleaseId.get(offer.matchedReleaseIds[0]!) ??
                    formats(offer.formatCategory ?? "other")}
                </span>
                <span className="block truncate text-xs text-muted-foreground">{offer.title}</span>
                <span className="block text-xs text-muted-foreground">
                  {offer.providerDisplayName}
                </span>
              </span>
              <span className="shrink-0 text-right text-base font-semibold">
                <Price amount={offer.currentPrice} currency={offer.currentCurrency} />
                <span className="block text-xs text-primary">{t("viewThisEdition")}</span>
              </span>
              <ArrowForwardIcon aria-hidden="true" className="shrink-0" />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
